import { IHttpResponse, IPromise, IQService, IScope } from "angular";
import { StateService } from "@uirouter/angularjs";
import { IDataSourceBlueprint } from "@gtmhub/datasources/models/models";
import { DataSourceService } from "@gtmhub/datasources/services/data-source.service";
import { IEntityFieldsMap, IFieldsMap } from "@gtmhub/entities/models/models";
import { EntityService } from "@gtmhub/entities/services/entity.service";
import { IIndicator, IUIError } from "@gtmhub/error-handling";
import { IInsightboard } from "@gtmhub/insightboards";
import {
  IConnectionData,
  IInsight,
  IInsightUsedEntity,
  InsightSelectorService,
  InsightService,
  UpdateInsightSelectorDataActionParams,
  isInsightCodeless,
} from "@gtmhub/insights";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { ParameterService } from "@gtmhub/parameters/parameterService";
import { PlanningSessionsActions } from "@gtmhub/sessions/redux/session-actions";
import { ISessionsStoreState } from "@gtmhub/sessions/redux/session-reducer";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { INgRedux } from "@gtmhub/state-management";
import { byTextField } from "@gtmhub/util/sorting-utils";
import { ICollection } from "@webapp/core/core.models";
import { Parameter } from "@webapp/data-story/models/parameters-facade.models";
import { IMetricSettings, Metric } from "@webapp/okrs/metrics/models/metric.models";
import { Session } from "@webapp/sessions/models/sessions.model";
import { IUpdateMetricField } from "./metric";

export interface IDynamicMetricScope extends IUpdateMetricField, IScope {
  metric: Metric;
  insight: IInsight;
  usage: IInsightboard[];
  sessions: Record<string, Session>;
  sessionsFetched: boolean;
  sessionsError: IUIError;
  permissions: {
    insights: boolean;
  };
  indicators: {
    loading?: IIndicator;
    loadingParams?: IIndicator;
  };
  openInsight(): void;
  showInsightDetails: boolean;
  toggleInsightDetails(): void;
}

const bindStateToScope = (state: ISessionsStoreState) => ({
  sessions: state.sessions.map,
  sessionsFetched: state.sessions.isFetched,
  sessionsError: state.sessions.error,
});

export class DynamicMetricCtrl {
  public static $inject = [
    "$q",
    "$scope",
    "$rootScope",
    "$state",
    "DataSourceService",
    "EntityService",
    "InsightService",
    "ParameterService",
    "$ngRedux",
    "PlanningSessionsActions",
    "InsightSelectorService",
  ];

  constructor(
    private $q: IQService,
    private $scope: IDynamicMetricScope & IExtendedRootScopeService,
    private $rootScope: IGtmhubRootScopeService,
    private $state: StateService,
    private dataSourceService: DataSourceService,
    private entityService: EntityService,
    private insightService: InsightService,
    private parameterService: ParameterService,
    private $ngRedux: INgRedux,
    private planningSessionsActions: PlanningSessionsActions,
    private insightSelectorService: InsightSelectorService
  ) {
    this.$scope.indicators = {};

    this.$scope.openInsight = this.openInsight;
    this.$scope.usage = [];
    this.$scope.toggleInsightDetails = () => (this.$scope.showInsightDetails = !this.$scope.showInsightDetails);

    this.$scope.$watch("metric", () => this.init());
  }

  private init = (): void => {
    const isDynamicMetric = !!this.$scope.metric.insightName;

    if (isDynamicMetric) {
      // used to hide insight and boards details if the user does not have permissions to any of them (not the best implementation, temporary)
      this.$scope.permissions = {
        insights: false,
      };

      this.getInsight();
    }
  };

  private getInsight = () => {
    this.$scope.indicators.loading = { progress: true };

    this.insightService
      .getInsightSafe(this.$scope.metric.insightName)
      .then((insight) => {
        this.$scope.insight = insight;

        if (this.$rootScope.hasPermission("ManageData")) {
          return this.getInsightUsage();
        }
      })
      .then(() => {
        const blueprint = this.getDataSourceBlueprintName(this.$scope.insight);

        if (blueprint) {
          return this.loadConnectionData(blueprint);
        } else {
          this.$scope.insight.connection = {
            name: null,
            title: null,
            logoUrl: "",
          };
        }
      })
      .then(
        () => {
          delete this.$scope.indicators.loading;

          if (this.$rootScope.hasPermission("ManageData")) {
            this.setMetricSettings();
          }
          // only sets a display value for 'session' metric settings filter using planning session data
          this.loadSessionFilterDisplayValues();
        },
        (rejection: IHttpResponse<unknown>) => {
          delete this.$scope.indicators.loading;

          const isForbidden = rejection.status === 403;
          if (!isForbidden) {
            this.$scope.indicators.loading = { error: rejection };
          }
        }
      );
  };

  private openInsight = (): void => {
    if (this.$rootScope.isMsTeamsState || !this.$rootScope.hasPermission("ManageData") || this.$scope.insight.connection.name === "powerbi") {
      return;
    }

    if (isInsightCodeless(this.$scope.insight)) {
      this.openBuildInsightModal();
    } else {
      this.$state.go("gtmhub.insight", {
        name: this.$scope.insight.name,
      });
    }
  };

  private openBuildInsightModal = (): void => {
    this.insightSelectorService
      .openBuildInsightModal({ insight: this.$scope.insight, connectionData: this.$scope.insight.connection, canGoBack: false })
      .then((params) => {
        if (params.action === "updateInsightSelectorData") {
          this.updateSelectorData(params);
        }
      });
  };

  private loadConnectionData = (datasourceBlueprintName: string) => {
    return this.dataSourceService.getBlueprint(datasourceBlueprintName).then((blueprint: IDataSourceBlueprint) => {
      this.$scope.insight.connection = this.getInsightConnectionData(blueprint);
    });
  };

  private getInsightConnectionData = (blueprint: IDataSourceBlueprint): IConnectionData => {
    return {
      name: blueprint.name,
      title: blueprint.title,
      logoUrl: blueprint.logoUrl,
    };
  };

  private getDataSourceBlueprintName = (insight: IInsight): string => {
    return insight.usedEntities && insight.usedEntities.length ? insight.usedEntities[0].dataSourceBlueprintName : null;
  };

  private updateSelectorData = (params: UpdateInsightSelectorDataActionParams): void => {
    this.$scope.insight = params.insight as IInsight;

    const partialMetric: Partial<Metric> = {
      actual: params.calculatedValue,
      insightName: params.insight.name,
      fieldName: params.fieldName,
      settings: params.settings
        .map<IMetricSettings>((x) => ({
          name: x.name,
          title: x.title,
          value: x.value,
          type: "text",
        }))
        .sort(byTextField("title")),
    };
    this.$scope.updateField(partialMetric, "settings");
  };

  private getInsightUsage = () => {
    return this.insightService.getUsageOnBoards(this.$scope.insight.id).then((usage) => {
      this.$scope.usage = usage;

      this.$scope.permissions.insights = true;
    });
  };

  private setMetricSettings = (): void => {
    const metricHasFilters = this.$scope.metric && this.$scope.metric.settings && this.$scope.metric.settings.length > 0;
    if (metricHasFilters) {
      this.$scope.indicators.loadingParams = { progress: true };

      if (!isInsightCodeless(this.$scope.insight)) {
        this.parameterService.getParameters().then(
          (parameters: ICollection<Parameter>) => {
            delete this.$scope.indicators.loadingParams;
            const insightFilters: Parameter[] = this.insightService.getInsightUsedFilters(this.$scope.insight, parameters.items);
            this.formatMetricSettingsUsingInsightParams(insightFilters);
          },
          (error) => (this.$scope.indicators.loadingParams = { error })
        );
      } else {
        // could be much simplified if field titles were persisted in BE
        this.getEntityFieldsMap().then(
          (entityFieldsMap: IEntityFieldsMap) => {
            delete this.$scope.indicators.loadingParams;
            this.formatMetricSettingsUsingEntityMap(entityFieldsMap);
          },
          (error) => (this.$scope.indicators.loadingParams = { error })
        );
      }
    }
  };

  private getEntityFieldsMap = (): IPromise<IEntityFieldsMap> => {
    const entityFieldsMap: IEntityFieldsMap = {};
    const usedEntities: IInsightUsedEntity[] = this.$scope.insight.usedEntities ? this.$scope.insight.usedEntities.filter((entity) => !!entity.name) : [];
    let promiseCount = 0;

    const deferred = this.$q.defer<IEntityFieldsMap>();

    if (usedEntities.length === 0) {
      deferred.resolve(entityFieldsMap);
    }

    for (let i = 0; i < usedEntities.length; i++) {
      const entityName: string = usedEntities[i].name;

      this.entityService.getEntity(entityName).then(
        (entity) => {
          if (entity.fields) {
            entityFieldsMap[entityName] = entity.fields;
          }
          if (i === promiseCount) {
            deferred.resolve(entityFieldsMap);
          }
          promiseCount++;
        },
        (err) => {
          deferred.reject(err);
        }
      );
    }

    return deferred.promise;
  };

  private formatMetricSettingsUsingEntityMap = (entityFieldsMap: IEntityFieldsMap) => {
    const requiredFieldsMap: IFieldsMap = this.getRequiredFieldsMap(entityFieldsMap);

    this.$scope.metric.settings = this.$scope.metric.settings
      .map((filter) => {
        return {
          name: filter.name,
          title: this.getFilterTitle(filter, requiredFieldsMap, entityFieldsMap),
          value: filter.value,
          type: "text",
        };
      })
      .sort(byTextField("title"));
  };

  private getFilterTitle = (filter: IMetricSettings, requiredFieldsMap: IFieldsMap, entityFieldsMap: IEntityFieldsMap) => {
    if (filter.title) {
      return filter.title;
    } else if (requiredFieldsMap && requiredFieldsMap[filter.name]) {
      return requiredFieldsMap[filter.name].title;
    } else {
      let entityTitle = filter.name;
      if (entityFieldsMap) {
        // fallback in case requiredFields are not properly persisted in insight's usedEntities in BE
        // could possible result in wrong title due to match between field name in different entity
        // cannot be resolved until BE properly saves requiredFields in usedEntities and/ or metric settings titles are persisted
        Object.keys(entityFieldsMap).forEach((entityName: string) => {
          const field = entityFieldsMap[entityName].find((field) => field.name === filter.name);
          if (field) {
            entityTitle = field.title;
          }
        });
      }
      return entityTitle;
    }
  };

  private getRequiredFieldsMap = (entityFieldsMap: IEntityFieldsMap): IFieldsMap => {
    const requiredFieldsMap: IFieldsMap = {};

    if (!this.$scope.insight.usedEntities) {
      return requiredFieldsMap;
    }

    this.$scope.insight.usedEntities.forEach((entity) => {
      entity.requiredFields.forEach((requiredField) => {
        requiredFieldsMap[requiredField.name] = entityFieldsMap[entity.name]?.find((field) => field.name === requiredField.name);
      });
    });

    return requiredFieldsMap;
  };

  private formatMetricSettingsUsingInsightParams = (insightFilters: Parameter[]): void => {
    const formattedFilters: IMetricSettings[] = [];

    // insight filters are leading because metric filters can contain outdated filters no longer available in insight
    insightFilters.forEach((insightFilter) => {
      const formattedFilter: IMetricSettings = {
        name: insightFilter.key,
        title: insightFilter.title,
        value: insightFilter.defaultValue,
        valueDisplayName: "",
        type: insightFilter.selectorType,
      };

      const metricFilter = this.getFilterFromMetricSettings(insightFilter.key);

      if (metricFilter) {
        formattedFilter.value = metricFilter.value;
        formattedFilter.valueDisplayName = metricFilter.valueDisplayName;
      }

      formattedFilters.push(formattedFilter);
    });

    // sort so that filters are always in alphabetic order
    this.$scope.metric.settings = formattedFilters.sort(byTextField("title"));
  };

  private getFilterFromMetricSettings = (name: string): IMetricSettings => {
    return this.$scope.metric.settings.find((filter) => filter.name === name);
  };

  private loadSessionFilterDisplayValues = () => {
    if (this.$scope.metric.settings.some((f) => f.name === "session" && f.value)) {
      this.$scope.indicators.loadingParams = { progress: true };
      this.getSessions()
        .then(
          () => {
            this.$scope.metric.settings.forEach((filter) => {
              // filter value could possibly contain a string of session ids => on error response defaults to filter's value
              const sessionsIds = filter.value.split(",");
              const displayNames = [];
              sessionsIds.forEach((sessionId) => {
                if (sessionId in this.$scope.sessions) {
                  displayNames.push(this.$scope.sessions[sessionId].title);
                } else {
                  // don't display error, just default to using the available session filter value coming from metric settings
                  displayNames.push(filter.value);
                }
              });
              filter.valueDisplayName = displayNames.join(", ");
            });
          },
          () => {
            this.$scope.indicators.loadingParams = { error: this.$scope.sessionsError };
          }
        )
        .finally(() => {
          delete this.$scope.indicators.loadingParams;
        });
    }
  };

  private getSessions(): IPromise<void> {
    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);
    this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());

    return this.$scope.watchUntilAndRejectOnError(
      () => this.$scope.sessionsFetched,
      () => this.$scope.sessionsError
    );
  }
}
